/**
 * Reading the cart and building the handoff payload.
 *
 * `GET /{locale}/cart.js` returns the cart as JSON, all money in cents, in the customer's
 * presentment currency (https://shopify.dev/docs/api/ajax/reference/cart, `docs/SHOPIFY.md`
 * §7.1). The whole document is forwarded verbatim: the service persists it as
 * `Checkout.sourceCart` and its zod schema is a loose object precisely so theme and app keys
 * survive the round trip (`docs/CART-VALIDATION.md` §2). The money in it is a *claim*, kept
 * only so the checkout can say "this price changed while you were shopping"; every total is
 * recomputed server-side from the Admin API.
 */

import type { PeakExperimentHint } from './experiment';
import { hasGaAnalytics, type YcGaAnalytics } from './ga-cookies';
import type { PeakMarketContext } from './market';
import type { PeakTrackBeeHint } from './trackbee';

/** The default cart endpoint. Locale-prefixed shops override it from the Liquid snippet. */
export const PEAK_DEFAULT_CART_URL = '/cart.js';

/**
 * The handoff body, exactly as `POST /api/checkout/create` expects it
 * (`docs/CHECKOUT-CREATE.md` §9).
 */
export type YcCreateCheckoutPayload = {
  publicKey: string;
  shopDomain: string;
  cart: Record<string, unknown>;
  /**
   * The shop's Google Analytics ids and Google Ads click ids, when the tag set them
   * (`./ga-cookies.ts`, `./google-click-cookies.ts`). Absent — not an empty object — when
   * there are none, so a shopper who declined the cookies sends a payload with no trace of
   * the field. The server treats anything it cannot validate here
   * as absent; it never rejects a handoff over attribution data.
   */
  analytics?: YcGaAnalytics;
  /**
   * The shopper's Shopify market from Liquid `localization`, as rendered onto our tag
   * (`./market.ts`, D210), plus `cartUrl`, the absolute URL `cart.js` was fetched from
   * (`withCartUrl`). A tag without the market attributes sends `cartUrl` alone; absent — not
   * an empty object — only when there is neither.
   */
  market?: PeakMarketContext;
  /**
   * The A/B-experiment unit id (`_peak_vid`, consent-gated), the QA forced arm (`?peak_arm=`, a
   * signed token) and `consent: false` when analytics was declined, `./experiment.ts`,
   * `docs/EXPERIMENTS.md` §4 (D239). Top-level, not inside the strict `analytics` block. Absent
   * — not an empty object — when there is none of them.
   */
  experiment?: PeakExperimentHint;
  /**
   * A tap an installed inline early-tap stub held before the script ran, replayed by the script
   * (D343; no stub printed since D360). Only ever `true`; absent otherwise, so a regular
   * handoff's body is unchanged. The endpoint logs it and nothing else reads it.
   */
  early?: true;
  /**
   * TrackBee's visitor id (`_t_i`), its timestamp (`_t_i_timestamp`) and the shopper's Customer
   * Privacy answer, for a store with TrackBee on (`./trackbee.ts`, D367). Top-level, never inside
   * the strict `analytics` block, so a server without the member ignores it rather than dropping
   * the Google ids with it. Absent — not an empty object — when there is nothing to send.
   */
  trackbee?: PeakTrackBeeHint;
};

/** Is this a `cart.js` document we can hand off? */
export function isUsableCart(cart: unknown): cart is Record<string, unknown> {
  if (typeof cart !== 'object' || cart === null || Array.isArray(cart)) return false;
  const record = cart as Record<string, unknown>;

  // `item_count` is the documented total quantity of items. An empty cart goes to the cart page
  // (D375) rather than create a checkout the customer cannot pay for.
  if (typeof record.item_count === 'number' && record.item_count <= 0) return false;
  if (Array.isArray(record.items) && record.items.length === 0) return false;

  return true;
}

export function buildCreatePayload(
  publicKey: string,
  shopDomain: string,
  cart: Record<string, unknown>,
  analytics?: YcGaAnalytics,
  market?: PeakMarketContext,
  experiment?: PeakExperimentHint,
  early = false,
  trackbee?: PeakTrackBeeHint,
): YcCreateCheckoutPayload {
  const payload: YcCreateCheckoutPayload = { publicKey, shopDomain, cart };
  if (analytics !== undefined && hasGaAnalytics(analytics)) payload.analytics = analytics;
  if (market !== undefined) payload.market = market;
  if (
    experiment !== undefined &&
    (experiment.unit || experiment.forceArm || experiment.consent === false)
  ) {
    payload.experiment = experiment;
  }
  if (early) payload.early = true;
  if (
    trackbee !== undefined &&
    (trackbee.visitorId !== undefined || trackbee.consent !== undefined)
  ) {
    payload.trackbee = trackbee;
  }
  return payload;
}

/** The URL the handoff is POSTed to. */
export function createEndpointUrl(serviceUrl: string): string {
  return serviceUrl + '/api/checkout/create';
}

/**
 * `window.Shopify`, as much of it as this bundle ever reads.
 *
 * Shopify's Online Store sets a global `Shopify` object on every storefront page; the two
 * members below are the only ones we touch. Everything is typed as `unknown` and checked at
 * runtime because the bundle also runs where there is no Shopify at all — the mock storefront,
 * a headless front end, a preview harness — and a missing global must degrade, never throw.
 */
type ShopifyGlobalLike = { shop?: unknown; routes?: unknown };

function asObject(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

/**
 * `window.Shopify.shop` — the shop's permanent `*.myshopify.com` domain, or `null`.
 *
 * This is the value `POST /api/checkout/create` matches the tenant's stored shop domain
 * against, which is why it is preferred over everything else: the global is set by Shopify and
 * cannot disagree with the connection, while a hand-written attribute or the page hostname can.
 */
export function shopifyShopDomain(shopifyGlobal: unknown): string | null {
  const global = asObject(shopifyGlobal) as ShopifyGlobalLike | null;
  if (global === null) return null;
  const shop = global.shop;
  if (typeof shop !== 'string') return null;
  const trimmed = shop.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * `window.Shopify.routes.root` — `/` on an ordinary shop, `/nl-nl/` behind Shopify Markets.
 *
 * Returned with a guaranteed trailing slash so {@link cartUrlFromRoutesRoot} can append to it,
 * and only when it looks like a root path at all; anything else (absent, not a string, not
 * rooted) is `null` and the caller falls back to {@link PEAK_DEFAULT_CART_URL}.
 */
export function shopifyRoutesRoot(shopifyGlobal: unknown): string | null {
  const global = asObject(shopifyGlobal) as ShopifyGlobalLike | null;
  if (global === null) return null;
  const routes = asObject(global.routes);
  if (routes === null) return null;
  const root = routes.root;
  if (typeof root !== 'string') return null;

  const trimmed = root.trim();
  if (!trimmed.startsWith('/')) return null;
  return trimmed.endsWith('/') ? trimmed : trimmed + '/';
}

/** `/` → `/cart.js`, `/nl-nl/` → `/nl-nl/cart.js` (`docs/SHOPIFY.md` §7.1). */
export function cartUrlFromRoutesRoot(root: string): string {
  return root + 'cart.js';
}

/**
 * The shop's canonical `*.myshopify.com` domain.
 *
 * {@link shopifyShopDomain} first, then the `data-peak-shop` attribute (which the theme snippet
 * renders from `{{ shop.permanent_domain }}`) for the rare theme that strips the global, and the
 * page hostname as the last resort — the service rejects a mismatch, so a wrong guess fails
 * closed into Peak's notice (D385) rather than into someone else's tenant.
 */
export function resolveShopDomain(
  shopifyGlobal: unknown,
  shopAttribute: string | null,
  hostname: string,
): string {
  const fromGlobal = shopifyShopDomain(shopifyGlobal);
  if (fromGlobal !== null) return fromGlobal;
  if (shopAttribute !== null && shopAttribute.length > 0) return shopAttribute;
  return hostname;
}
